import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEntity } from '@backstage/plugin-catalog-react';
import { EntityWarningContent } from '@internal/backstage-plugin-devops-react';
import { CloudResourceEntity, RuleEntity } from '@internal/backstage-plugin-catalog-devops-common';
import {
  convertDateFormat,
  filterCloudFields,
  useRulesByEntity,
} from '@internal/backstage-plugin-devops-react';
import {
  maxSavingOf,
  pillClass,
  resourceSeverityOf,
  severityRank,
  ruleTitle,
  siblingTabPath,
} from './shared';
import styles from './entity-page.module.css';

const humanizeKey = (key: string): string =>
  key.replace(/([a-z0-9])([A-Z])/g, '$1 $2');

/**
 * CloudResource page Overview, 1:1 with design/cloud-resource-page-mockup.html:
 * resource-scoped scan-error alert, stat strip, cloudFields attributes and
 * inventory tags as key-value tables, failed-rules preview (severity is the
 * value THIS resource carries in each rule's spec.resources[]).
 */
export function CloudResourceOverview() {
  const { entity } = useEntity();
  const resource = entity as CloudResourceEntity;
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { rules } = useRulesByEntity(resource);
  const ruleEntities = useMemo(() => rules as RuleEntity[], [rules]);

  const resourceName =
    resource.metadata.name || resource.metadata.title || '';
  const errors = resource.spec.errors ?? [];

  const failedRows = useMemo(
    () =>
      ruleEntities
        .filter(rule => rule.spec.result === 'Failed')
        .map(rule => ({
          rule,
          severity: resourceSeverityOf(rule, resourceName),
          saving: maxSavingOf(rule),
        }))
        .sort(
          (a, b) => severityRank(a.severity) - severityRank(b.severity),
        ),
    [ruleEntities, resourceName],
  );

  const worstSeverity = failedRows[0]?.severity;

  const attributeEntries = useMemo(() => {
    const records = filterCloudFields(resource);
    return Object.entries(records).map(([key, value]) => ({
      key: humanizeKey(key),
      value: String(value),
    }));
  }, [resource]);

  const tagEntries = useMemo(() => {
    const tags = resource.spec.cloudFields?.Tags;
    if (Array.isArray(tags)) {
      return tags
        .map(tag => {
          const record = tag as { Key?: string; key?: string; Value?: string; value?: string };
          return {
            key: String(record.Key ?? record.key ?? ''),
            value: String(record.Value ?? record.value ?? ''),
          };
        })
        .filter(tag => tag.key);
    }
    return [];
  }, [resource]);

  const half = Math.ceil(attributeEntries.length / 2);
  const attributeColumns = [
    attributeEntries.slice(0, half),
    attributeEntries.slice(half),
  ].filter(column => column.length > 0);

  return (
    <>
      <EntityWarningContent />

      {errors.length > 0 && (
        <div className={styles.alert}>
          <span aria-hidden="true">⚠</span>
          <span>
            <b>
              {errors.length} scan {errors.length === 1 ? 'error' : 'errors'}
            </b>{' '}
            affect{errors.length === 1 ? 's' : ''} this resource —{' '}
            {String(errors[0])}
          </span>
          <span className={styles.alertWhat}>details on the account page</span>
        </div>
      )}

      <div className={styles.statStrip}>
        <div className={`${styles.card} ${styles.stat}`}>
          <span className={styles.statK}>Failed rules</span>
          <span className={styles.statV}>{failedRows.length}</span>
        </div>
        <div className={`${styles.card} ${styles.stat}`}>
          <span className={styles.statK}>Highest severity</span>
          <span className={styles.statV}>{worstSeverity ?? '—'}</span>
        </div>
        <div className={`${styles.card} ${styles.stat}`}>
          <span className={styles.statK}>Scan errors</span>
          <span className={styles.statV}>{errors.length}</span>
        </div>
        <div className={`${styles.card} ${styles.stat}`}>
          <span className={styles.statK}>Last updated</span>
          <span className={styles.statV}>
            {convertDateFormat(resource.spec.resourcesUpdateTime) || '—'}
          </span>
        </div>
      </div>

      <div className={styles.row2}>
        <div className={`${styles.card} ${styles.cardPad}`}>
          <div className={styles.cardHead}>
            <h3 className={styles.cardTitle}>Attributes</h3>
            <span className={styles.cardHint}>spec.cloudFields</span>
          </div>
          {attributeEntries.length > 0 ? (
            <div className={styles.kvCols}>
              {attributeColumns.map((column, index) => (
                <table key={index} className={styles.kvTable}>
                  <tbody>
                    {column.map(entry => (
                      <tr key={entry.key}>
                        <td className={styles.kvKey}>{entry.key}</td>
                        <td className={styles.kvValue}>{entry.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ))}
            </div>
          ) : (
            <p className={styles.muted}>No attributes collected.</p>
          )}
        </div>

        <div className={`${styles.card} ${styles.cardPad}`}>
          <div className={styles.cardHead}>
            <h3 className={styles.cardTitle}>Tags</h3>
            <span className={styles.cardHint}>inventory Tags</span>
          </div>
          {tagEntries.length > 0 ? (
            <table className={styles.kvTable}>
              <tbody>
                {tagEntries.map(tag => (
                  <tr key={tag.key}>
                    <td className={styles.kvKey}>{tag.key}</td>
                    <td className={styles.kvValue}>{tag.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className={styles.muted}>No tags.</p>
          )}
        </div>
      </div>

      <div className={styles.secTitle}>Failed rules on this resource</div>
      <div className={styles.card}>
        <table className={styles.table} aria-label="Failed rules preview">
          <tbody>
            {failedRows.slice(0, 3).map(row => (
              <tr
                key={row.rule.metadata.name}
                className={styles.row}
                onClick={() =>
                  navigate(`/catalog/default/rule/${row.rule.metadata.name}`)
                }
              >
                <td style={{ width: 90 }}>
                  <span
                    className={`${styles.pill} ${pillClass(styles, row.severity)}`}
                  >
                    <span className={styles.pillDot} />
                    {row.severity}
                  </span>
                </td>
                <td>
                  <span className={styles.ruleName}>
                    {ruleTitle(row.rule)}
                  </span>{' '}
                  <span className={styles.ruleCode}>{row.rule.spec.type}</span>
                </td>
                <td className={styles.muted}>{row.rule.spec.category}</td>
                <td style={{ textAlign: 'right' }}>
                  {row.saving ? (
                    <span className={styles.saving}>${row.saving}/mo</span>
                  ) : (
                    <span className={styles.muted}>—</span>
                  )}
                </td>
                <td className={styles.cellArr} style={{ width: 30 }}>
                  ↗
                </td>
              </tr>
            ))}
            {failedRows.length === 0 && (
              <tr>
                <td className={styles.muted}>
                  Nothing failed on this resource.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {failedRows.length > 0 && (
          <div className={styles.tableFoot}>
            <button
              type="button"
              className={styles.cardGo}
              onClick={() => navigate(siblingTabPath(pathname, 'rules'))}
            >
              View all rules →
            </button>
          </div>
        )}
      </div>
    </>
  );
}
